import { Component, DoCheck, Input, IterableDiffers, OnDestroy } from '@angular/core';
import { ElementTagName, objectOrientationToolList, objectOrientation } from '@models/editor';
import { SelectedElementsService } from '@service/canvas/selected-elements.service';
import { defaultNumberInputArrowedKey } from '@service/key-filter.service';

@Component({
    selector: 'app-object-orientation',
    templateUrl: './object-orientation.component.html',
    styleUrls: ['./object-orientation.component.css'],
    standalone: false,
})
export class ObjectOrientationComponent {
    @Input() selectedElements;

    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly OBJECT_ORIENTATION_TOOL_LIST = objectOrientationToolList;
    readonly TRANSLATE_PREFIX = 'mimic.design.object.orientation.label';

    xPosition: string;
    yPosition: string;

    readonly POSITION_X = 'X';
    readonly POSITION_Y = 'Y';

    iterableDiffer;
    observer: MutationObserver;

    xMax: number;
    yMax: number;

    isGTagDragActionActive: boolean = false;

    constructor(
        private iterableDiffers: IterableDiffers,
        private selectionService: SelectedElementsService
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    private getBBoxInParentCoordinates(element: any) {
        const bbox = element.getBBox();
        const transformList = element.transform?.baseVal;
        if (!transformList || transformList.numberOfItems === 0) return bbox;

        let combined = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
        for (let i = 0; i < transformList.numberOfItems; i++) {
            const m = transformList.getItem(i).matrix;
            combined = {
                a: combined.a * m.a + combined.c * m.b,
                b: combined.b * m.a + combined.d * m.b,
                c: combined.a * m.c + combined.c * m.d,
                d: combined.b * m.c + combined.d * m.d,
                e: combined.a * m.e + combined.c * m.f + combined.e,
                f: combined.b * m.e + combined.d * m.f + combined.f,
            };
        }

        const corners = [
            { x: bbox.x, y: bbox.y },
            { x: bbox.x + bbox.width, y: bbox.y },
            { x: bbox.x, y: bbox.y + bbox.height },
            { x: bbox.x + bbox.width, y: bbox.y + bbox.height },
        ];
        const transformedXs = corners.map(p => combined.a * p.x + combined.c * p.y + combined.e);
        const transformedYs = corners.map(p => combined.b * p.x + combined.d * p.y + combined.f);
        const xMin = Math.min(...transformedXs);
        const yMin = Math.min(...transformedYs);
        return {
            x: xMin,
            y: yMin,
            width: Math.max(...transformedXs) - xMin,
            height: Math.max(...transformedYs) - yMin,
        };
    }

    private getSelectionBBox() {
        if (this.selectedElements.length === 1) return this.getBBoxInParentCoordinates(this.selectedElements[0]);

        let xMin = Infinity;
        let yMin = Infinity;
        let xMax = -Infinity;
        let yMax = -Infinity;

        this.selectedElements.forEach((el: any) => {
            const bbox = this.getBBoxInParentCoordinates(el);
            xMin = Math.min(xMin, bbox.x);
            yMin = Math.min(yMin, bbox.y);
            xMax = Math.max(xMax, bbox.x + bbox.width);
            yMax = Math.max(yMax, bbox.y + bbox.height);
        });

        return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
    }

    private flipRotationTransform(element: any, isHorizontal: boolean, pivot: number) {
        const transform = element.getAttribute('transform') || '';
        const match = transform.match(
            /rotate\(\s*(-?\d+(?:\.\d+)?)(?:\s*[,\s]\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?))?\s*\)/
        );
        if (!match) return;
        const angle = Number(match[1]);
        const cx = match[2] !== undefined ? Number(match[2]) : 0;
        const cy = match[3] !== undefined ? Number(match[3]) : 0;
        const newCx = isHorizontal ? 2 * pivot - cx : cx;
        const newCy = isHorizontal ? cy : 2 * pivot - cy;
        const newRotate = `rotate(${-angle}, ${newCx}, ${newCy})`;
        element.setAttribute('transform', transform.replace(match[0], newRotate));
    }

    private flipHorizontal(pivotX: number) {
        if (!this.selectedElements || this.selectedElements.length === 0) return;

        this.selectedElements.forEach((element: any) => {
            const tagName = element.tagName;

            if (tagName === ElementTagName.line) {
                const x1 = Number(element.getAttribute('x1'));
                const x2 = Number(element.getAttribute('x2'));
                element.setAttribute('x1', 2 * pivotX - x1);
                element.setAttribute('x2', 2 * pivotX - x2);
                return;
            }

            if (tagName === ElementTagName.ellipse) {
                const cx = Number(element.getAttribute('cx'));
                element.setAttribute('cx', 2 * pivotX - cx);
                this.flipRotationTransform(element, true, pivotX);
                return;
            }

            if (tagName === ElementTagName.rect) {
                const x = Number(element.getAttribute('x'));
                const w = Number(element.getAttribute('width'));
                element.setAttribute('x', 2 * pivotX - (x + w));
                this.flipRotationTransform(element, true, pivotX);
                return;
            }
            const prevTransform = element.getAttribute('transform') || '';
            const flipTransform = `matrix(-1, 0, 0, 1, ${2 * pivotX}, 0)`;
            element.setAttribute('transform', `${flipTransform} ${prevTransform}`.trim());
        });

        this.selectionService.setSelectedElementsForSaveButton(this.selectedElements);
    }

    private flipVertical(pivotY: number) {
        if (!this.selectedElements || this.selectedElements.length === 0) return;

        this.selectedElements.forEach((element: any) => {
            const tagName = element.tagName;

            if (tagName === ElementTagName.line) {
                const y1 = Number(element.getAttribute('y1'));
                const y2 = Number(element.getAttribute('y2'));
                element.setAttribute('y1', 2 * pivotY - y1);
                element.setAttribute('y2', 2 * pivotY - y2);
                return;
            }

            if (tagName === ElementTagName.ellipse) {
                const cy = Number(element.getAttribute('cy'));
                element.setAttribute('cy', 2 * pivotY - cy);
                this.flipRotationTransform(element, false, pivotY);
                return;
            }

            if (tagName === ElementTagName.rect) {
                const y = Number(element.getAttribute('y'));
                const h = Number(element.getAttribute('height'));
                element.setAttribute('y', 2 * pivotY - (y + h));
                this.flipRotationTransform(element, false, pivotY);
                return;
            }

            const prevTransform = element.getAttribute('transform') || '';
            const flipTransform = `matrix(1, 0, 0, -1, 0, ${2 * pivotY})`;
            element.setAttribute('transform', `${flipTransform} ${prevTransform}`.trim());
        });
        this.selectionService.setSelectedElementsForSaveButton(this.selectedElements);
    }

    moveElement({ name }) {
        if (!this.selectedElements || this.selectedElements.length === 0) return;

        const bbox = this.getSelectionBBox();

        switch (name) {
            case objectOrientation.horizontal:
                const pivotX = bbox.x + bbox.width / 2;
                this.flipHorizontal(pivotX);
                break;
            case objectOrientation.vertical:
                const pivotY = bbox.y + bbox.height / 2;
                this.flipVertical(pivotY);
                break;
            default:
                console.error('object orientation not found');
        }
    }
}
